import type {
  Cookie,
  SelectedCookie,
} from '@design-system/components/Cookie/CookiebannerCenter.type'
import { notLoadCookieBanner } from '@integration-layer/utils/oneTrustLocales'

/**
 * Global window interface extension for OneTrust integration.
 * @global
 */
declare global {
  interface Window {
    OneTrust: OneTrust
  }
}

/**
 * Represents the OneTrust object with its available methods.
 * @typedef {Object} OneTrust
 */
type OneTrust = {
  /**
   * Retrieves domain-specific cookie data.
   * @returns {Object} Domain data containing cookie groups
   */
  GetDomainData: () => {
    Groups: OneTrustCookie[]
  }

  /**
   * Rejects all cookie preferences.
   */
  RejectAll: () => void

  /**
   * Allows all cookie preferences.
   */
  AllowAll: () => void

  /**
   * Checks if the alert box is closed and valid.
   * @returns {boolean} Alert box status
   */
  IsAlertBoxClosedAndValid: () => boolean

  /**
   * Changes the language for cookie preferences.
   * @param {string} language - Target language code
   */
  changeLanguage: (language: string) => void

  /**
   * Updates user consent for specific cookie categories.
   * @param {string} type - Consent type
   * @param {string} cookies - Serialized cookie preferences
   */
  UpdateConsent: (type: string, cookies: string) => void

  /**
   * Renders the cookie list table into #ot-sdk-cookie-policy.
   * Replaces existing content, so calling it twice does not duplicate the list.
   */
  initializeCookiePolicyHtml: () => void
}

/**
 * Represents a OneTrust cookie group configuration.
 * @typedef {Object} OneTrustCookie
 */
type OneTrustCookie = {
  /** Unique group identifier */
  OptanonGroupId: string

  /** Group name */
  GroupName: string

  /** Group description */
  GroupDescription: string

  /** Current status of the cookie group */
  Status: 'always active' | 'active' | 'inactive'

  /** List of cookies in the group */
  Cookies: any[]
}

/**
 * List of locales where cookie banner should not be shown.
 * @type {string[]}
 */
const notShowCookieBanner = ['en-us', 'es-us']

/**
 * Combined list of locales where cookie banner is neither loaded nor shown.
 * @type {string[]}
 */
const notShowOrLoadCookieBanner = [
  ...notLoadCookieBanner,
  ...notShowCookieBanner,
]

/**
 * Composable for managing OneTrust cookie preferences and consent.
 *
 * @returns {Object} Cookie management utilities
 * @property {Ref<Cookie[]>} cookies - List of available cookie groups
 * @property {Function} rejectAll - Rejects all optional cookies
 * @property {Function} acceptAll - Accepts all cookies
 * @property {Function} changeCookieLanguage - Changes cookie banner language
 * @property {Function} toggleCookie - Toggles individual cookie group selection
 * @property {Function} save - Saves current cookie preferences
 * @property {Ref<boolean>} isLoaded - Indicates if OneTrust script is loaded
 * @property {string[]} notLoadCookieBanner - Locales where the banner is not loaded (from utils/oneTrustLocales.ts)
 * @property {Function} loadScript - Loads OneTrust script and initializes cookie settings
 *
 * @example
 * const {
 *   cookies,
 *   acceptAll,
 *   toggleCookie,
 *   save
 * } = useOneTrustCookies()
 */
export const useOneTrustCookies = () => {
  // for federated app support
  const isLoaded = useState('is_loaded_cookie', () => false)
  const { eventBus } = useFederatedEventBus()
  const { countryLocale } = useCountryLocale()
  const { country } = useRouteHelper()

  const shouldLoad = computed(
    () =>
      !!countryLocale.value &&
      !notLoadCookieBanner.includes(countryLocale.value)
  )

  // Script is now loaded directly in nuxt.config.ts head for early loading
  // Only load via composable if script is not already present (fallback)
  if (shouldLoad.value && import.meta.client) {
    const existingScript = document.querySelector('script[src*="otSDKStub.js"]')
    if (!existingScript) {
      useScript(
        {
          src: 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
          'data-domain-script':
            country.toLowerCase() === 'us'
              ? '0192b849-42e6-7972-b5cf-8308303c610c'
              : '01919d3c-be13-74ce-a59e-ed8763aa05ee',
          async: true,
        },
        {
          trigger: 'client',
        }
      )
    }
  }

  let oneTrust = import.meta.client ? window.OneTrust : undefined
  const checkOneTrust = () => {
    oneTrust = import.meta.client ? window.OneTrust : undefined
  }

  const { language } = useRouteHelper()
  const { openDialog: openCookieBanner } = useDialog('modal-cookie-banner')
  const cookies = useState<Cookie[]>('ONE_TRUST_COOKIES', () => [])

  const allowedCookies = computed<SelectedCookie[]>(() =>
    cookies.value.map(cookie => ({
      id: cookie.id,
      selected: cookie.selected ?? false,
    }))
  )

  const checkSavedConsent = () => {
    const cookieValue = useCookie('OptanonConsent').value
    if (cookieValue) {
      const cookieParams = new URLSearchParams(cookieValue)

      const groups = cookieParams.get('groups')

      if (groups) {
        const groupPairs = groups.split(',').map(group => {
          const [id, value] = group.split(':')
          return { id, value: parseInt(value) }
        })

        return groupPairs
      }
    }
  }

  const loadScript = async () => {
    if (!shouldLoad.value) {
      return
    }

    await waitUntil(
      () => !!window.OneTrust,
      () => {
        isLoaded.value = true
        checkOneTrust()
        const oneTrustCookie = oneTrust!
          .GetDomainData()
          .Groups.filter(data => data.Cookies.length)
        cookies.value = oneTrustCookie.map(cookie => ({
          id: cookie.OptanonGroupId,
          title: cookie.GroupName,
          text: cookie.GroupDescription,
          offButton: cookie.Status !== 'always active',
          defaultSelected: cookie.Status === 'always active' ? true : undefined,
          selected: cookie.Status === 'always active' ? true : undefined,
        }))
        oneTrust!.changeLanguage(language)
        if (
          !oneTrust!.IsAlertBoxClosedAndValid() &&
          !notShowOrLoadCookieBanner.includes(countryLocale.value)
        ) {
          openCookieBanner()
        } else {
          eventBus?.$emit('privacyAcceptanceEvent', true)
          const savedCookies = checkSavedConsent()
          if (savedCookies?.length) {
            savedCookies.forEach(cookieSaved => {
              const item = cookies.value.find(
                cookie => cookie.id === cookieSaved.id
              )
              item &&
                item.offButton === true &&
                (item.selected = cookieSaved.value === 1)
            })
          }
        }
      },
      100,
      10_000
    )
  }

  const toggleCookie = (id: string, selected: boolean) => {
    cookies.value = cookies.value.map(cookie =>
      cookie.id === id ? { ...cookie, selected } : cookie
    )
  }

  const save = () => {
    const oneTrustConsentCookies = allowedCookies.value
      .map(item => `${item.id}:${item.selected ? 1 : 0}`)
      .join(',')

    checkOneTrust()
    oneTrust?.UpdateConsent?.('Category', oneTrustConsentCookies)
  }

  const acceptAll = () => {
    cookies.value.forEach(cookie => (cookie.selected = true))
    checkOneTrust()
    eventBus?.$emit('privacyAcceptanceEvent', true)
    oneTrust?.AllowAll?.()
  }

  const rejectAll = () => {
    cookies.value.forEach(cookie => {
      if (cookie.offButton) {
        cookie.selected = false
      }
    })
    checkOneTrust()
    eventBus?.$emit('privacyAcceptanceEvent', false)
    oneTrust?.RejectAll?.()
  }

  const changeCookieLanguage = (language: string) => {
    checkOneTrust()
    oneTrust?.changeLanguage?.(language)
  }

  return {
    cookies,
    rejectAll,
    acceptAll,
    changeCookieLanguage,
    toggleCookie,
    save,
    isLoaded,
    notLoadCookieBanner,
    loadScript,
  }
}
